import React, { useCallback, useMemo } from "react";
import { graphql, useFragment, usePaginationFragment } from "react-relay";
import { useHistory } from "react-router";

import { faBars, faChevronDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { captureException } from "@sentry/react";
import { format, getMinutes } from "date-fns";
import { compact } from "lodash";

import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getDateString } from "~/lib/utils";
import { environment } from "~/models";
import { IconButton } from "~/ui/button/IconButton";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";

import type { SeriesExplorer_note$key } from "~/__generated__/SeriesExplorer_note.graphql";
import type { SeriesExplorer_notes$key } from "~/__generated__/SeriesExplorer_notes.graphql";
import type { SeriesExplorerNextNoteMutation } from "~/__generated__/SeriesExplorerNextNoteMutation.graphql";
import {
	default as seriesExplorerNotesRefetchQuery,
	type SeriesExplorerNotesRefetchQuery,
} from "~/__generated__/SeriesExplorerNotesRefetchQuery.graphql";

import styles from "./SeriesExplorer.module.css";

function formatMeetingDate(startTime: Date, isAllDay: boolean) {
	// eslint-disable-next-line max-len
	return `${getDateString(startTime)}${!isAllDay ? ` at ${format(startTime, `h${getMinutes(startTime) > 0 ? ":mm" : ""} a`)}` : ""}`;
}

graphql`
	fragment SeriesExplorer_linkable_note on Note @relay(mask: false) {
		id
		createdAt
		eventStart
		url
		event {
			isAllDay
		}
	}
`;

const noteFragment = graphql`
	fragment SeriesExplorer_note on Note {
		id
		stream {
			id
			nextEvent {
				start
				end
				isAllDay
			}
		}
		previousNote {
			...SeriesExplorer_linkable_note @relay(mask: false)
		}
	}
`;

const paginationFragment = graphql`
	fragment SeriesExplorer_notes on NoteStream @refetchable(queryName: "SeriesExplorerNotesRefetchQuery") {
		notes(
			first: $first
			after: $after
			last: $last
			before: $before
			currentEventGuid: $eventGuid
			startFromNote: $startFromNote
		) @connection(key: "SeriesExplorer_notes", filters: []) {
			pageInfo {
				endCursor
				hasPreviousPage
				startCursor
			}

			edges {
				cursor
				node {
					...SeriesExplorer_linkable_note @relay(mask: false)
				}
			}
		}
	}
`;

function LoadingMeetings() {
	return (
		<>
			<Menu.LoadingItem animationDelayIndex={0} />
			<Menu.LoadingItem animationDelayIndex={1} />
			<Menu.LoadingItem animationDelayIndex={2} />
			<Menu.LoadingItem animationDelayIndex={3} />
			<Menu.LoadingItem animationDelayIndex={4} />
		</>
	);
}

function PreviousMeetings({ streamId, previousNoteId }: { streamId: string; previousNoteId: string }) {
	const data = useLazyLoadQuery<SeriesExplorerNotesRefetchQuery>(
		seriesExplorerNotesRefetchQuery,
		{ id: streamId, first: 10, startFromNote: previousNoteId },
		{ fetchPolicy: "store-and-network" },
	);
	const pagination = usePaginationFragment<SeriesExplorerNotesRefetchQuery, SeriesExplorer_notes$key>(
		paginationFragment,
		data.node,
	);

	const notes = useMemo(
		() =>
			compact(
				pagination.data?.notes?.edges.map(
					edge =>
						edge?.node &&
						edge.node.url &&
						edge.node.createdAt && {
							id: edge.node.id,
							start: edge.node.eventStart ? new Date(edge.node.eventStart) : new Date(edge.node.createdAt),
							isAllDay: edge.node.event?.isAllDay ?? false,
							url: edge.node.url,
						},
				),
			),
		[pagination.data],
	);

	const trackOtherMeetingClick = useCallback(() => {
		dispatchAnalyticsTrack("Opened other meeting in series", { location: "series_explorer", futureMeeting: false });
	}, []);

	const onLoadAfter = useCallback(() => {
		if (!pagination.hasNext) return;
		pagination.loadNext(10);
	}, [pagination]);

	return (
		<>
			{notes.map(note => (
				<Menu.ItemLink key={note.id} to={note.url} onClick={trackOtherMeetingClick}>
					{note.start ? formatMeetingDate(note.start, note.isAllDay) : "Unscheduled note"}
				</Menu.ItemLink>
			))}
			{pagination.hasNext &&
				(pagination.isLoadingNext ? (
					<LoadingMeetings />
				) : (
					<>
						<button className={styles.loadMoreButton} onClick={onLoadAfter}>
							Load more&ensp;
							<FontAwesomeIcon icon={faChevronDown} fontSize="0.8em" />
						</button>
					</>
				))}
		</>
	);
}

export default function SeriesExplorer({ note: noteKey }: { note: SeriesExplorer_note$key }) {
	const note = useFragment(noteFragment, noteKey);
	const history = useHistory();

	const onSelectCreateNote = useCallback(
		async (withEvent: boolean) => {
			const { NoteStream_CreateNextNote: result } = await commitMutationPromise<SeriesExplorerNextNoteMutation>(
				environment,
				{
					mutation: graphql`
						mutation SeriesExplorerNextNoteMutation($id: ID!, $withEvent: Boolean) {
							NoteStream_CreateNextNote(id: $id, withEvent: $withEvent) {
								noteEdge {
									cursor
									node {
										url
									}
								}
							}
						}
					`,
					variables: { id: note.stream.id, withEvent },
				},
			);

			if (!result || !result.noteEdge?.node?.url) {
				toast.error("Could not create next note in the series.");
				captureException(new Error("Could not create next note in the series."), {
					extra: {
						noteId: note.id,
						withEvent,
					},
				});
				return;
			}

			history.push(result.noteEdge.node.url);
		},
		[note.id, note.stream.id, history],
	);

	const onSelectNextMeeting = useCallback(() => {
		onSelectCreateNote(true);
		dispatchAnalyticsTrack("Opened other meeting in series", { location: "series_explorer", futureMeeting: true });
	}, [onSelectCreateNote]);

	const onSelectUnscheduledNote = useCallback(() => {
		onSelectCreateNote(false);
		dispatchAnalyticsTrack("Opened other meeting in series", { location: "series_explorer", futureMeeting: true });
	}, [onSelectCreateNote]);

	const nextEventTimeFormatted = useMemo(() => {
		const nextEvent = note.stream.nextEvent;
		if (!nextEvent || !nextEvent.start) return null;

		return formatMeetingDate(new Date(nextEvent.start), nextEvent.isAllDay);
	}, [note.stream.nextEvent]);

	const previousMeeting = useMemo(() => {
		if (!note.previousNote || !note.previousNote.createdAt || !note.previousNote.url) return null;

		return {
			id: note.previousNote.id,
			start: note.previousNote.eventStart
				? new Date(note.previousNote.eventStart)
				: new Date(note.previousNote.createdAt),
			isAllDay: note.previousNote.event?.isAllDay ?? false,
			url: note.previousNote.url,
		};
	}, [note.previousNote]);

	return (
		<Menu>
			<Menu.Trigger asChild>
				<IconButton icon={faBars} variant="tertiary" />
			</Menu.Trigger>
			<Menu.Content>
				<Menu.Sub>
					<Menu.SubTrigger>New meeting</Menu.SubTrigger>
					<Menu.SubContent>
						{nextEventTimeFormatted && (
							// This "next meeting" creates a new note at the end of the series, and
							// is not the following note in the series, which may be a bit confusing.
							<Menu.Item onSelect={onSelectNextMeeting}>{nextEventTimeFormatted} (Next meeting)</Menu.Item>
						)}
						<Menu.Item onSelect={onSelectUnscheduledNote}>Unscheduled note</Menu.Item>
					</Menu.SubContent>
				</Menu.Sub>
				{previousMeeting && (
					<>
						<Menu.Separator />
						<Menu.Label>Previous meetings</Menu.Label>
						<div className={styles.previousMeetings}>
							<React.Suspense
								fallback={
									<>
										<Menu.ItemLink to={previousMeeting.url}>
											{formatMeetingDate(previousMeeting.start, previousMeeting.isAllDay)}
										</Menu.ItemLink>
										<LoadingMeetings />
									</>
								}
							>
								<PreviousMeetings streamId={note.stream.id} previousNoteId={previousMeeting.id} />
							</React.Suspense>
						</div>
					</>
				)}
			</Menu.Content>
		</Menu>
	);
}
